<template>
  <div class="timelapse-controls">
    <div class="controls-row">
        <button class="control-btn" @click="$emit('prev')" title="Precedente">
            <SkipBackIcon :size="20" />
        </button>
        <button class="control-btn play-btn" @click="$emit('toggle-play')" :title="isPlaying ? 'Pausa' : 'Play'">
            <PauseIcon v-if="isPlaying" :size="24" />
            <PlayIcon v-else :size="24" align="center" style="margin-left: 2px" />
        </button>
        <button class="control-btn" @click="$emit('next')" title="Successivo">
            <SkipForwardIcon :size="20" />
        </button>
    </div>
    
    <div class="timeline-row">
        <input 
            type="range" 
            :min="0" 
            :max="totalFrames - 1" 
            :value="currentFrameIndex" 
            @input="onSliderInput"
            class="timeline-slider"
            :disabled="totalFrames <= 1"
        />
    </div>

    <div class="date-display">
        {{ currentDateFormatted }}
        <span class="index-display">{{ currentFrameIndex + 1 }} / {{ totalFrames }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { PlayIcon, PauseIcon, SkipBackIcon, SkipForwardIcon } from 'lucide-vue-next';

const props = defineProps<{
    isPlaying: boolean;
    currentDate: string;
    availableDates: string[];
}>();

const emit = defineEmits(['toggle-play', 'prev', 'next', 'seek']);

const totalFrames = computed(() => props.availableDates.length);
const currentFrameIndex = computed(() => {
    const idx = props.availableDates.indexOf(props.currentDate);
    return idx === -1 ? 0 : idx;
});

const currentDateFormatted = computed(() => {
    if (!props.currentDate) return 'Latest';
    const d = new Date(props.currentDate);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
});

function onSliderInput(e: Event) {
    const val = Number((e.target as HTMLInputElement).value);
    const date = props.availableDates[val];
    if (date) {
        emit('seek', date);
    }
}
</script>

<style scoped>
.timelapse-controls {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(30, 35, 41, 0.9);
    backdrop-filter: blur(8px);
    border: 1px solid #444;
    border-radius: 12px;
    padding: 10px 15px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 320px;
    z-index: 1000;
    box-shadow: 0 4px 20px rgba(0,0,0,0.4);
    pointer-events: auto;
}

.controls-row {
    display: flex;
    justify-content: center;
    gap: 15px;
    align-items: center;
}

.control-btn {
    background: none;
    border: none;
    color: #ccc;
    cursor: pointer;
    padding: 5px;
    border-radius: 50%;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.control-btn:hover {
    color: #fff;
    background: rgba(255,255,255,0.1);
}

.play-btn {
    background: #0088cc;
    color: white;
    width: 40px;
    height: 40px;
}

.play-btn:hover {
    background: #0099e6;
    transform: scale(1.1);
}

.timeline-row {
    width: 100%;
    display: flex;
    align-items: center;
}

.timeline-slider {
    width: 100%;
    height: 4px;
    -webkit-appearance: none;
    background: #444;
    border-radius: 2px;
    outline: none;
    cursor: pointer;
}

.timeline-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    background: #0088cc;
    border-radius: 50%;
    border: 2px solid #fff;
    cursor: pointer;
    transition: transform 0.1s;
}

.timeline-slider::-webkit-slider-thumb:hover {
    transform: scale(1.2);
}

.date-display {
    text-align: center;
    font-size: 0.9em;
    font-weight: bold;
    color: #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.index-display {
    font-size: 0.8em;
    color: #888;
    font-weight: normal;
}
</style>
